import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Reads current URL search params. Re-evaluates when location changes.
 */
function useSearchParams() {
  const location = useLocation();
  return React.useMemo(() => new URLSearchParams(location.search), [location.search]);
}

/**
 * Returns a function that updates URL query params via history.replace
 * (no new history entry). Merges with existing params.
 */
function useUpdateSearchParams() {
  const history = useHistory();
  const location = useLocation();

  return React.useCallback((updates: Record<string, string | string[] | null>) => {
    // Read from window.location to get the latest params (avoids stale closures
    // when multiple params are updated in the same event handler)
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(updates)) {
      params.delete(key);
      if (value === null || value === undefined) {
        // param deleted
      } else if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v));
      } else {
        params.set(key, value);
      }
    }
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [history, location.pathname]);
}

/**
 * A useState-like hook that syncs an array value with a URL query parameter.
 * On mount, initializes from the URL. On set, updates both state and URL.
 */
export function useArrayQueryParam(paramName: string): [string[], (v: string[] | ((prev: string[]) => string[])) => void] {
  const searchParams = useSearchParams();
  const updateParams = useUpdateSearchParams();
  const initial = React.useMemo(() => searchParams.getAll(paramName), []);
  const [state, _setState] = React.useState<string[]>(initial);

  const setState = React.useCallback((value: string[] | ((prev: string[]) => string[])) => {
    _setState(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      updateParams({ [paramName]: next.length > 0 ? next : null });
      return next;
    });
  }, [paramName, updateParams]);

  return [state, setState];
}

/**
 * A useState-like hook that syncs a string|null value with a URL query parameter.
 * On mount, initializes from the URL. On set, updates both state and URL.
 */
export function useStringQueryParam(paramName: string): [string | null, (v: string | null) => void] {
  const searchParams = useSearchParams();
  const updateParams = useUpdateSearchParams();
  const initial = React.useMemo(() => searchParams.get(paramName), []);
  const [state, _setState] = React.useState<string | null>(initial);

  const setState = React.useCallback((value: string | null) => {
    _setState(value);
    updateParams({ [paramName]: value });
  }, [paramName, updateParams]);

  return [state, setState];
}
